import { MARKETING_SECTION_TYPES, formatCount, tt } from '@agos/shared'

/**
 * The marketing page the platform publishes, and the editor that publishes it.
 *
 * `GET /super-admin/marketing-pages/{slug}` answers **two** section lists: `sections`, which is live, and
 * `draftSections`, which is what somebody is working on. Measured on `home`: **9** published sections —
 * `hero`, `features`, `logos`, `pricing`, `gallery`, `testimonials`, `faq`, `cta`, `footer` — and
 * `draftSections: null`.
 *
 * **`null` is not "a draft that differs".** The obvious check is `sections !== draftSections`, and on
 * this data it is true — so an editor written that way announces unpublished changes on a page nobody
 * has touched, and the operator publishes to make the warning go away. A draft exists or it does not,
 * and only then is it worth comparing.
 *
 * `publishedAt` equals `updatedAt` to the millisecond here, which is what *nothing edited since publish*
 * looks like. It is a second signal rather than the primary one: an edit that changes nothing still
 * moves `updatedAt`.
 *
 * **A section type this build cannot draw is named, not dropped** — FB-166's lesson, which has now cost
 * this migration twice. The marketing site renders its own set; a type it does not know would vanish
 * from a published page with nothing said.
 */

export interface MarketingSection {
  id: string
  type: string
  props: Record<string, unknown>
}

/**
 * What the marketing site is drawn with - §3.11.
 *
 * **This was dropped entirely**, and it is not decoration: `apps/marketing/src/site.ts` turns
 * `primaryColor` and `secondaryColor` into `--brand` and `--brand-secondary`, and reads the font, the
 * theme, the radius and the tab icon. So the public site's brand was decided by a field no screen in the
 * product could reach.
 *
 * **`logo` was in this list and the shopfront drew it nowhere** - §3.181, FB-954. The sentence above used to
 * say *reads the font and the logo*, which was false from the day the field was ported: `grep logo` over the
 * whole shopfront finds the `logos` section, a partner list with its own per-item URLs, and nothing else.
 * `faviconUrl` replaces it, which is what legacy's editor offers and legacy's renderer reads.
 *
 * `domain` lives in the same object and is **read but never written here** - `POST …/domain`
 * provisions a proxy host on the real Nginx Proxy Manager, which §5 governs. It is read so the page
 * can say which address it claims.
 */
export interface SiteSettings {
  domain: string | null
  /** §3.181 - the published page's own tab icon, over §3.180's static default. */
  faviconUrl: string | null
  /**
   * §3.182 - the body face, one of `SITE_FONTS`, or null for the stylesheet's own.
   *
   * **`font` is still read and is no longer written.** A blob from before the split carries one key meaning
   * both, so `bodyFont` falls back to it rather than losing a stored choice; two keys meaning one thing is
   * how a blob comes to disagree with itself, so the first save on the new controls retires it.
   */
  bodyFont: string | null
  /** §3.182 - the display face, legacy's own second select. Never settable before this. */
  headingFont: string | null
  primaryColor: string | null
  secondaryColor: string | null
  /** §3.166 - `dark | light | warm`, or null for the design system's own. */
  theme: string | null
  /** §3.166 - `sharp | medium | round`, or null. */
  radius: string | null
}

/**
 * The themes and corner radii the public site can draw - §3.166.
 *
 * **Legacy's own sets, ported exactly.** `marketing-site/src/index.css` implements these and no others, so a
 * fourth value would be an option with no rules behind it - §3.132's defect, where a control wrote a key the
 * renderer did not read.
 */
export const SITE_THEMES = ['dark', 'light', 'warm'] as const
export const SITE_RADII = ['sharp', 'medium', 'round'] as const

/**
 * The families the public site actually preloads - §3.182, FB-956.
 *
 * **Read off `apps/marketing/src/index.css`'s own `@import`, and held to it by
 * `MarketingFontsAgreeTest`.** The control this replaces was a free-text `<input placeholder="Font">`, and
 * `cssVariables` writes any string into `--font-body` - so an operator could type `Lora` or
 * `Playfair Display`, see it saved and returned, and get the system sans on the public page, because those
 * are **legacy's** five and not AGOS's. Only `Inter` is in both systems' lists, which makes the five names
 * an operator who used legacy would reach for exactly the five that render as nothing.
 *
 * **Not legacy's set.** `apps/super-admin/src/index.css` preloads the same five, so the shopfront inherited
 * the product's font set rather than legacy's marketing one. Which five a public page may draw is a design
 * choice with the owner's name on it, and swapping in legacy's six would change what their site can look
 * like and add six downloads to a public page's critical path - so the loadable set is left alone and the
 * question is banked, the division §3.180 drew about the favicon's colour.
 *
 * No label map: a font's name **is** what the operator reads, unlike a theme's `warm`.
 */
export const SITE_FONTS = ['Inter', 'Outfit', 'Poppins', 'Roboto', 'Nunito'] as const

/**
 * What the operator reads, rather than the word stored - §3.166.
 *
 * **Exhaustive rather than a map with a fall-through**, which is §9.512's guarantee: a fourth theme added to
 * the set above becomes a compile error here instead of drawing `warm` under a label nobody chose.
 */
export function themeLabel(theme: (typeof SITE_THEMES)[number]): string {
  switch (theme) {
    case 'dark':
      return tt('Tmavý')
    case 'light':
      return tt('Svetlý')
    case 'warm':
      return tt('Teplý')
  }
}

export function radiusLabel(radius: (typeof SITE_RADII)[number]): string {
  switch (radius) {
    case 'sharp':
      return tt('Ostré')
    case 'medium':
      return tt('Stredné')
    case 'round':
      return tt('Oblé')
  }
}

export interface MarketingPage {
  slug: string
  title: string
  description: string
  sections: MarketingSection[]
  /** `null` when nobody has started a draft — which is different from a draft matching the live page. */
  draftSections: MarketingSection[] | null
  settings: SiteSettings
  /**
   * The whole `site_settings` object as it was read.
   *
   * Kept beside the typed view because a save must **merge** rather than replace: the column is one
   * `jsonb` blob, `save` assigns it whole, and a fresh object built from the five fields above would
   * drop anything legacy or a later version put there. `domain` is already such a field for this
   * screen - it is read and never set.
   */
  rawSettings: Record<string, unknown>
  publishedAt: string | null
  updatedAt: string | null
}

/**
 * The section types the marketing site can render today.
 *
 * **§3.45 - one list now, shared with the public renderer.** This and `RENDERABLE` in
 * `apps/marketing` were two hand-maintained copies of the same nine strings, deployed as two
 * separate applications: a type here and not there publishes a section the public site drops
 * without saying so. Kept as a re-export under its own name so every reader here is unchanged.
 */
export const KNOWN_SECTIONS = MARKETING_SECTION_TYPES

function sectionsOf(value: unknown): MarketingSection[] | null {
  if (!Array.isArray(value)) return null
  return value.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return []
    const row = entry as Record<string, unknown>
    const type = typeof row.type === 'string' ? row.type : ''
    if (type === '') return []
    return [
      {
        id: typeof row.id === 'string' ? row.id : type,
        type,
        props: typeof row.props === 'object' && row.props !== null ? (row.props as Record<string, unknown>) : {},
      },
    ]
  })
}

export function marketingPageOf(payload: unknown): MarketingPage | null {
  const outer = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  const row = (typeof outer.page === 'object' && outer.page !== null ? outer.page : outer) as Record<string, unknown>
  const slug = typeof row.slug === 'string' ? row.slug : ''
  if (slug === '') return null
  const text = (key: string): string => (typeof row[key] === 'string' ? row[key] : '')
  const optional = (key: string): string | null =>
    typeof row[key] === 'string' && row[key] !== '' ? row[key] : null
  return {
    slug,
    title: text('title'),
    description: text('description'),
    sections: sectionsOf(row.sections) ?? [],
    draftSections: sectionsOf(row.draftSections),
    settings: settingsOf(row.siteSettings),
    rawSettings:
      typeof row.siteSettings === 'object' && row.siteSettings !== null
        ? (row.siteSettings as Record<string, unknown>)
        : {},
    publishedAt: optional('publishedAt'),
    updatedAt: optional('updatedAt'),
  }
}

function settingsOf(value: unknown): SiteSettings {
  const row = (typeof value === 'object' && value !== null ? value : {}) as Record<string, unknown>
  const field = (key: string): string | null =>
    typeof row[key] === 'string' && row[key] !== '' ? row[key] : null
  return {
    domain: field('domain'),
    faviconUrl: field('faviconUrl'),
    /* §3.182 - `oneOf` for the reason the theme and radius below carry it: a family the site does not preload
       would be written onto the root while rendering as the system sans, which is the comment §3.166 already
       makes about a value its CSS has no rules for. The fallback to `font` is the migration read. */
    bodyFont: oneOf(field('bodyFont') ?? field('font'), SITE_FONTS),
    headingFont: oneOf(field('headingFont') ?? field('font'), SITE_FONTS),
    primaryColor: field('primaryColor'),
    secondaryColor: field('secondaryColor'),
    /* §3.166 - **a value outside the set reads as absent**, so a blob somebody edited by hand cannot put the
     * site into a state its CSS has no rules for. That is the other half of not offering a fourth option. */
    theme: oneOf(field('theme'), SITE_THEMES),
    radius: oneOf(field('radius'), SITE_RADII),
  }
}

/** The value if the renderer knows it, else null - §3.166. */
function oneOf(value: string | null, allowed: readonly string[]): string | null {
  return value !== null && allowed.includes(value) ? value : null
}

/** Whether anybody has started a draft at all. */
export function hasDraft(page: MarketingPage): boolean {
  return page.draftSections !== null
}

/**
 * Whether there is something unpublished worth publishing.
 *
 * A draft must **exist** and **differ**. Comparing a `null` draft against the live sections is how an
 * editor invents work: it reports changes on a page nobody has touched, and the operator publishes to
 * clear the warning.
 */
export function hasUnpublishedChanges(page: MarketingPage): boolean {
  if (page.draftSections === null) return false
  return JSON.stringify(page.draftSections) !== JSON.stringify(page.sections)
}

export function draftNote(page: MarketingPage): string {
  if (!hasDraft(page)) return tt('Žiadny rozpracovaný koncept - zobrazuje sa publikovaná verzia.')
  if (!hasUnpublishedChanges(page)) return tt('Koncept je totožný s publikovanou verziou.')
  return tt('Máte nepublikované zmeny.')
}

/** What is on the page now, or what the draft would make it. */
export function shownSections(page: MarketingPage, viewingDraft: boolean): MarketingSection[] {
  return viewingDraft && page.draftSections !== null ? page.draftSections : page.sections
}

export function unrenderable(sections: readonly MarketingSection[]): MarketingSection[] {
  return sections.filter((section) => !(KNOWN_SECTIONS as readonly string[]).includes(section.type))
}

export function unknownSectionNote(sections: readonly MarketingSection[]): string {
  const missing = unrenderable(sections)
  if (missing.length === 0) return ''
  return `${tt('Tieto sekcie sa na stránke nevykreslia')}: ${missing.map((section) => section.type).join(', ')}.`
}

export function sectionSummary(sections: readonly MarketingSection[]): string {
  return `${formatCount(sections.length)} ${tt('sekcií')}`
}

/**
 * Whether publishing would change anything.
 *
 * Offered only when there is a draft that differs — a publish button that is always live invites a
 * click that republishes the same page and moves `publishedAt` for no reason.
 */
export function mayPublish(page: MarketingPage): boolean {
  return hasUnpublishedChanges(page)
}

// ─── §3.11 - the three writes, and the asymmetry between two of them ──────────────────────────────

/**
 * What `MarketingService.save` does with a body, in its own words:
 *
 * > *"Title, description and site settings **do go live immediately**; only the sections are
 * > staged."*
 *
 * So one Save button is a false statement, and these are two different requests. `livePatch` carries
 * the fields that publish themselves; `draftPatch` carries the ones that wait. Neither ever carries
 * the other's keys - a body with both would publish half a change and stage the rest under one
 * click.
 */
export interface LiveDraft {
  title: string
  description: string
  /* §3.181, FB-954 - `logo` was here and the shopfront drew it nowhere. `faviconUrl` is what legacy's
     editor offers and legacy's renderer reads, and §3.180's static file is the default behind it. */
  faviconUrl: string
  /** §3.182 - legacy's own two selects, where AGOS had one free-text box over a set it could not draw. */
  bodyFont: string
  headingFont: string
  primaryColor: string
  secondaryColor: string
  /** Empty means *the design system's own*, which is what leaves the stored blob untouched - §3.166. */
  theme: string
  radius: string
}

export function liveDraftOf(page: MarketingPage): LiveDraft {
  return {
    title: page.title,
    description: page.description,
    faviconUrl: page.settings.faviconUrl ?? '',
    bodyFont: page.settings.bodyFont ?? '',
    headingFont: page.settings.headingFont ?? '',
    primaryColor: page.settings.primaryColor ?? '',
    secondaryColor: page.settings.secondaryColor ?? '',
    theme: page.settings.theme ?? '',
    radius: page.settings.radius ?? '',
  }
}

/**
 * The body for the fields that go live on save.
 *
 * **`siteSettings` is merged onto what was read, not rebuilt.** The column is one `jsonb` blob and
 * `save` assigns it whole, so a fresh object from the four fields here would delete `domain` - which
 * this screen deliberately does not set, and which is what tells the operator where the page is
 * served. An empty box clears its key rather than storing `""`, because the site reads an empty
 * string as absent anyway and a stored blank is a value somebody has to explain.
 */
export function livePatch(page: MarketingPage, draft: LiveDraft): Record<string, unknown> {
  const settings: Record<string, unknown> = { ...page.rawSettings }
  const set = (key: keyof LiveDraft) => {
    const value = draft[key].trim()
    if (value === '') delete settings[key]
    else settings[key] = value
  }
  set('faviconUrl')
  /* §3.182 - and `font` is deliberately NOT among these: it is read for a blob written before the split and
     never written back, so the first save on these controls deletes it rather than leaving two keys that mean
     one thing. `set` deletes an empty box, which is what makes that retirement free. */
  set('bodyFont')
  set('headingFont')
  /* §3.182 - **and the pre-split key is retired on any save of these fields, which is not tidiness.**
     `set` deletes an empty box, so choosing *Predvolené* removes `bodyFont` - and with `font` still in the
     blob both renderers fall back to it, so **the operator could not clear the face at all**. Found by an
     existing case that cleared `font` and expected the key gone. Deleting it here means the first save on
     these controls is also the migration, and the fallback in both parsers only ever serves a page nobody
     has saved since. */
  delete settings.font
  set('primaryColor')
  set('secondaryColor')
  /* §3.166 - and `set` is why an unchosen theme stores nothing: it **deletes** the key for an empty box
   * rather than writing `""`, so *the design system's own* and *dark, chosen* stay different facts. */
  set('theme')
  set('radius')
  return {
    title: draft.title.trim(),
    description: draft.description.trim(),
    siteSettings: settings,
  }
}

/** The body for the sections, which are staged. Sections and nothing else, deliberately. */
export function draftPatch(sections: readonly MarketingSection[]): Record<string, unknown> {
  return { sections: sections.map((section) => ({ ...section })) }
}

export function maySaveLive(page: MarketingPage, draft: LiveDraft): boolean {
  // The title is the one field the page cannot be without: `save` falls back to the slug on create,
  // and an empty one on an existing page would put the slug in the browser's tab.
  if (draft.title.trim() === '') return false
  return JSON.stringify(livePatch(page, draft)) !== JSON.stringify(livePatch(page, liveDraftOf(page)))
}

/**
 * Is there work on this screen the server has not agreed to - FB-939, `SAAS.md` §3.174.
 *
 * The editor holds an editing session on the **public website** in page-level state, with two explicit
 * save buttons, and until this existed a closed tab lost it with nothing said.
 *
 * **It reuses `maySaveLive`'s comparison and drops its title gate, on purpose.** That gate refuses a
 * blank title because the page cannot be saved without one - and a cleared title is still unsaved work
 * somebody would lose, so warning about it is the point. The one case the save rule gets wrong for this
 * question.
 *
 * **And `edited !== null` is the obvious rule and the wrong one.** `saved()` clears `sections` and not
 * `edited`, so that version would warn for ever about a page the server has already stored. Comparing
 * the patches asks the question that matters: does what is typed differ from what is kept.
 *
 * **The sections half is `sections !== null`**, which is exactly `data-save-draft`'s own `disabled`
 * condition - so the warning and the button that clears it agree by construction.
 */
export function hasUnsavedMarketingWork(
  page: MarketingPage,
  draft: LiveDraft,
  sections: readonly MarketingSection[] | null,
): boolean {
  if (sections !== null) return true
  return JSON.stringify(livePatch(page, draft)) !== JSON.stringify(livePatch(page, liveDraftOf(page)))
}

/** What saving the live fields will do, said before it is done. */
export function liveSaveNote(): string {
  return tt('Názov, popis a vzhľad sa zverejnia okamžite - bez publikovania.')
}

/** And what saving the sections will not do. */
export function draftSaveNote(): string {
  return tt('Sekcie sa uložia do konceptu. Na stránke sa objavia až po publikovaní.')
}

/**
 * Why publishing is refused, or null.
 *
 * **`no-draft` is the server's own refusal** - *"Žiadny draft - najprv uložte zmeny."* - and it is
 * the state the owner's page is in right now: `draft_sections` is NULL. A disabled button explains
 * none of that, which is what the dead control did.
 */
export type PublishRefusal = 'no-draft' | 'no-change' | null

export function publishRefusal(page: MarketingPage): PublishRefusal {
  if (!hasDraft(page)) return 'no-draft'
  return hasUnpublishedChanges(page) ? null : 'no-change'
}

export function publishRefusalMessage(refusal: PublishRefusal): string {
  if (refusal === null) return ''
  if (refusal === 'no-draft') {
    return tt('Nie je čo publikovať - najprv ulož zmenu v sekciách do konceptu.')
  }
  return tt('Koncept je totožný s publikovanou verziou - publikovaním by sa nič nezmenilo.')
}

/**
 * What publishing does, named rather than confirmed blankly.
 *
 * **§3.15 adds the drop count**, because publishing is the one irreversible step and the number that
 * matters there is how many sections come *off* the page - said only when it is non-zero, since a
 * publish that drops nothing announcing *"drops 0"* is noise in front of the one button that
 * matters.
 */
export function publishNote(page: MarketingPage): string {
  const count = page.draftSections?.length ?? 0
  const base = `${tt('Koncept sa stane živou stránkou')} - ${formatCount(count)} ${tt('sekcií na')} ${page.settings.domain ?? tt('doméne, ktorá nie je nastavená')}.`
  const dropped = droppedSections(page).length
  if (dropped === 0) return base
  return `${base} ${formatCount(dropped)} ${tt('sekcií sa zo stránky odstráni - po publikovaní ich už nebude odkiaľ vrátiť.')}`
}

/**
 * The sections with one moved.
 *
 * Returns the list unchanged at either end rather than wrapping: a move that silently sent the first
 * block to the bottom is §9.329's own rule for the manager's homepage blocks.
 */
export function moved(
  sections: readonly MarketingSection[],
  index: number,
  step: -1 | 1,
): MarketingSection[] {
  const next = index + step
  if (index < 0 || index >= sections.length || next < 0 || next >= sections.length) {
    return [...sections]
  }
  const copy = [...sections]
  const [lifted] = copy.splice(index, 1)
  copy.splice(next, 0, lifted!)
  return copy
}

export function withoutSection(
  sections: readonly MarketingSection[],
  id: string,
): MarketingSection[] {
  return sections.filter((section) => section.id !== id)
}

/** What removing a section takes off the page, said before it is done. */
export function removeNote(section: MarketingSection): string {
  return `${tt('Sekcia')} ${section.type} ${tt('zmizne zo stránky po publikovaní. Do konceptu sa uloží hneď.')}`
}

// ─── §3.15 - the way back from a removal ──────────────────────────────────────────────────────────

/**
 * What the published page has that the draft no longer does.
 *
 * **§3.11 built a one-way door.** `MarketingPage` computes
 * `editing = sections ?? page.draftSections ?? page.sections`, so once a removal is saved the draft
 * is what the editor reads and the section is unreachable from the screen. The published list still
 * holds it until publish - the whole point of the draft/published split - so the way back was on the
 * wire the entire time.
 *
 * **Only meaningful while a draft exists.** With `draftSections` null there is no draft to have
 * dropped anything, and reporting the whole published list as *dropped* would be FB-S01's shape
 * again: a true comparison saying something false.
 */
export function droppedSections(page: MarketingPage): MarketingSection[] {
  if (page.draftSections === null) return []
  const kept = new Set(page.draftSections.map((section) => section.id))
  return page.sections.filter((section) => !kept.has(section.id))
}

/**
 * A dropped section put back into the draft, **at the end**.
 *
 * Not at its original index: the draft has been reordered since, that position may not exist any
 * more, and appending is the only placement that needs no assumption. The arrows already on the
 * screen move it.
 *
 * A no-op for an id the published page does not have - there is nothing to restore from, and
 * inventing a section would be worse than the door this closes.
 */
export function withSectionRestored(
  page: MarketingPage,
  draft: readonly MarketingSection[],
  id: string,
): MarketingSection[] {
  const published = page.sections.find((section) => section.id === id)
  if (published === undefined || draft.some((section) => section.id === id)) {
    return [...draft]
  }
  return [...draft, { ...published }]
}

/** What restoring puts back, said before it is done. */
export function restoreNote(section: MarketingSection): string {
  return `${tt('Sekcia')} ${section.type} ${tt('sa vráti do konceptu na konec. Poradie zmeníš šípkami.')}`
}

/*
 * ── Attaching a domain - SAAS.md §3.20, the owner's #22 ─────────────────────────────────────────
 */

/** The server's ceiling, mirrored so the screen refuses the same length. */
const DOMAIN_LIMIT = 120

/**
 * A domain shape, deliberately narrower than the server's.
 *
 * `MarketingService.domain()` accepts `^[a-z0-9.-]+$`, so `-`, `...` and `a..b` all pass it and
 * each would become a proxy host on the real Nginx with a certificate requested for it. This wants
 * labels: at least two, none empty, none starting or ending with a hyphen, and a final label of
 * letters.
 */
const DOMAIN_SHAPE = /^(?=.{1,120}$)([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/

/** What a typed domain becomes before it is checked or sent - the server lowercases and trims too. */
export function normalizeDomain(typed: string): string {
  return typed.trim().toLowerCase()
}

/**
 * Why this domain cannot be attached, or `null` when it can.
 *
 * **The strictness is the point, and it is one-directional.** A refused typo costs a second
 * attempt; an accepted one costs a proxy host and a certificate request for a domain the platform
 * does not control, and `attachDomain` provisions before it records, so there is nothing to undo.
 */
export function domainProblem(typed: string): string | null {
  const domain = normalizeDomain(typed)
  if (domain === '') return tt('Zadajte doménu.')
  if (domain.length > DOMAIN_LIMIT) return tt('Doména je príliš dlhá.')
  if (domain.startsWith('http')) return tt('Zadajte len doménu, bez http:// a bez cesty.')
  if (!DOMAIN_SHAPE.test(domain)) return tt('Toto nie je platná doména - napríklad pizza-majstro.sk.')
  return null
}

/** Whether the confirmation's typed-back domain matches the one being attached. */
export function domainConfirmed(typed: string, typedAgain: string): boolean {
  const domain = normalizeDomain(typed)
  return domain !== '' && domain === normalizeDomain(typedAgain)
}

/**
 * What the confirmation says before real infrastructure is provisioned.
 *
 * It names the domain, says a certificate will be requested, and says the thing that makes the
 * typed-back box necessary: the proxy host is created **before** anything is recorded, so a typo
 * cannot be taken back from this screen.
 */
export function domainConfirmation(typed: string): string {
  return `${tt('Nasmeruje')} ${normalizeDomain(typed)} ${tt('na túto stránku na skutočnom Nginx a vyžiada certifikát. Proxy host sa vytvorí ešte pred zápisom, takže preklep sa odtiaľto nedá vrátiť.')}`
}

/**
 * What came back, said in full.
 *
 * **`certIssued` false is the answer worth surfacing**: the domain is attached and not yet served
 * over TLS, which is what somebody needs to know before handing the address to a customer.
 */
export function domainReport(payload: unknown): string {
  const row = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<
    string,
    unknown
  >
  const domain = typeof row.domain === 'string' ? row.domain : ''
  if (domain === '') return tt('Doména sa nenasmerovala - skúste to znova.')
  const host = row.reused === true ? tt('Existujúci proxy host bol nasmerovaný sem') : tt('Proxy host bol vytvorený')
  const tls =
    row.certIssued === true
      ? tt('certifikát je vydaný')
      : tt('certifikát ešte nie je vydaný - stránka zatiaľ nebeží po HTTPS')
  return `${domain}: ${host}, ${tls}.`
}

// ─── §3.66 - the content of a section, which nothing could set ────────────────────────────────────

/**
 * The fields a section of each type actually has drawn - `SAAS.md` §3.66, FB-665.
 *
 * **Measured off the renderer, not off legacy's form.** `apps/marketing/src/Sections.tsx` is the only
 * thing that decides whether a value appears on the public site: `SectionView` draws `title`,
 * `subtitle` and `eyebrow` for **every** type, and each `case` reads its own. So a field offered here
 * that the renderer does not read would be a box whose value nothing draws - which is the shape
 * FB-001 exists to stop, and the reason this list is derived rather than copied.
 *
 * **The server's own spelling**, which is the first alias in `site.ts`'s `FIELDS`: the renderer
 * prefers it and legacy's editor writes it, so a page edited here and a page edited there carry the
 * same keys. The aliases exist to read old content, not to write new.
 *
 * **Lists are deliberately absent** - `items`, `tiers`, `columns`, `links`. They are arrays of
 * objects with their own sub-fields and they want their own increment; §3.66 says so out loud rather
 * than leaving the omission to be discovered.
 */
export interface SectionField {
  key: string
  label: string
  kind: 'text' | 'multiline' | 'boolean' | 'choice'
  /**
   * The options, for `kind: 'choice'` - §3.142.
   *
   * **A select rather than a text box wherever the server knows the whole set**, which is §3.133's lesson:
   * legacy offers free text for `highlight`, the owner's own data carries a slug that matches nothing, and a
   * typo there looks set and does nothing.
   */
  choices?: readonly { value: string; label: string }[]
}

const EVERY_TYPE: SectionField[] = [
  { key: 'eyebrow', label: 'Eyebrow', kind: 'text' },
  { key: 'title', label: tt('Titulok'), kind: 'text' },
  { key: 'subtitle', label: tt('Podtitulok'), kind: 'multiline' },
]

const CTA_PAIR: SectionField[] = [
  { key: 'ctaText', label: tt('Hlavné CTA - text'), kind: 'text' },
  { key: 'ctaHref', label: tt('Hlavné CTA - odkaz'), kind: 'text' },
  { key: 'secondaryCtaText', label: tt('Vedľajšie CTA - text'), kind: 'text' },
  { key: 'secondaryCtaHref', label: tt('Vedľajšie CTA - odkaz'), kind: 'text' },
]

const PER_TYPE: Record<string, SectionField[]> = {
  /* §3.142 - `source` is read by `MarketingService.resolveLiveSources`, which fills the tiers from the real
     packages, and was offered by **nothing**: so a static price list - a promotional price, a tier not yet in the
     catalogue, a page frozen while the packages are reorganised - was unreachable except by editing the database.
     The owner's own section carries `live`, which is why the default matters more than the control. */
  /* §3.160 - **one CTA, not `CTA_PAIR`**, which is §3.131's precedent and its guard's reason: the tier draws a
     single button, so a secondary text and href would be two boxes nothing renders and
     `MarketingSectionFieldsAgreeTest` would fail naming them. Legacy has one button per tier too.
     Both boxes are optional and fall back to *Začať* and `APP_CONTACT_EMAIL`, because the owner's stored section
     carries neither and a field with no default would have shipped and changed nothing (FB-851). */
  pricing: [
    { key: 'ctaText', label: tt('CTA na tarife - text'), kind: 'text' },
    { key: 'ctaHref', label: tt('CTA na tarife - odkaz'), kind: 'text' },
    {
      key: 'source',
      label: tt('Zdroj tarifov'),
      kind: 'choice',
      choices: [
        { value: 'live', label: tt('Živé - z balíkov v Tower') },
        { value: 'static', label: tt('Vlastný zoznam nižšie') },
      ],
    },
  ],
  hero: [...CTA_PAIR, { key: 'imageUrl', label: tt('Obrázok (URL)'), kind: 'text' }],
  /* §3.130 - `inputPlaceholder` was found by the guard rather than by reading: the CTA's case draws
     `prop(section, 'inputPlaceholder')` and this list offered no box for it, so the e-mail field always showed
     the built-in default and legacy's editor has had *Placeholder emailu* all along. The mirror of the direction
     §3.66 worried about - the renderer drawing more than the screen offers. */
  /* FB-814, §3.150 - **`withInput` and `inputPlaceholder` are gone from here, and that is §3.130's rule
     pointing the other way.** That entry added the placeholder box because the renderer read the key and this
     list offered no control - the pair has to agree. §3.150 stopped the renderer drawing the field at all,
     because its form was `preventDefault` and a visitor's e-mail went nowhere, so the same rule now requires
     **neither**: a switch here would set a key the public site no longer obeys, which is §3.132's defect
     mirrored.
     `MarketingSectionFieldsAgreeTest` is what said so - it failed with
     `{"cta"=["withInput", "inputPlaceholder"]}` the moment the renderer stopped reading them, which is exactly
     the drift it exists to catch. The owner's stored `withInput: false` and `inputPlaceholder: 'test@test.sk'`
     stay in their JSON harmlessly, because nothing reads either.
     Owner question #89 carries the real feature: an endpoint, a table, and a decision about what happens to
     other people's contact details. The controls come back with it. */
  cta: [...CTA_PAIR],
  footer: [
    { key: 'brand', label: tt('Značka'), kind: 'text' },
    { key: 'tagline', label: tt('Podnadpis'), kind: 'text' },
    { key: 'copyright', label: 'Copyright', kind: 'text' },
  ],
  /* §3.130 - read off each new case in `Sections.tsx`, and now asserted against it rather than derived by hand:
     `MarketingSectionFieldsAgreeTest` covers these scalars as well as the lists.

     **`dismissible` is not here**, and legacy has it: it draws the word *dismissable* beside the message and
     provides no control, so the box would set a flag whose only effect is a label that is not true. */
  banner: [
    { key: 'text', label: tt('Text pásu'), kind: 'multiline' },
    { key: 'ctaText', label: tt('CTA - text'), kind: 'text' },
    { key: 'ctaHref', label: tt('CTA - odkaz'), kind: 'text' },
  ],
  quote: [
    { key: 'quote', label: tt('Citát'), kind: 'multiline' },
    { key: 'author', label: tt('Autor'), kind: 'text' },
    { key: 'role', label: tt('Rola alebo firma'), kind: 'text' },
    { key: 'avatarUrl', label: tt('Fotka (URL)'), kind: 'text' },
  ],
  image: [
    { key: 'imageUrl', label: tt('Obrázok (URL)'), kind: 'text' },
    { key: 'alt', label: tt('Alternatívny text'), kind: 'text' },
    { key: 'caption', label: tt('Popis pod obrázkom'), kind: 'text' },
  ],
  /* Legacy's five words, and its own fallback to the middle one when the value is anything else. */
  spacer: [{ key: 'size', label: tt('Veľkosť (xs, sm, md, lg, xl)'), kind: 'text' }],
  /* §3.131 - `body` rather than the chrome's `subtitle`, which is the judgement the entry is about. Labelled
     *text beside the image* so an operator can tell it from the strapline above, which also renders. */
  /* **One CTA, not `CTA_PAIR` - the guard caught this.** The first draft spread the pair in, and
     `MarketingSectionFieldsAgreeTest` failed naming `split: [secondaryCtaText, secondaryCtaHref]`: the case
     draws one `Cta`, so two of the four boxes would have been boxes nothing draws. Legacy's `SplitSection`
     destructures `ctaText` and `ctaHref` and no second pair either, so one is also the port. */
  split: [
    { key: 'ctaText', label: tt('CTA - text'), kind: 'text' },
    { key: 'ctaHref', label: tt('CTA - odkaz'), kind: 'text' },
    { key: 'body', label: tt('Text vedľa obrázka'), kind: 'multiline' },
    { key: 'imageUrl', label: tt('Obrázok (URL)'), kind: 'text' },
  ],
  /* §3.178 - **one field, and the allowed services are in its label.** `SectionField` has no `note`, and
     widening a shared descriptor type for one sentence is the wrong trade (§3.155); `spacer`'s label already
     names its five words the same way. The label tells an operator the constraint **before** they paste rather
     than leaving them to wonder why nothing drew.
     **`poster` is NOT here and legacy has it**: its renderer puts `poster` on an `<iframe>`, where the attribute
     does nothing at all, so the box sets a value that cannot have an effect - §3.130's `dismissable` and
     FB-814's e-mail field for the third time. */
  video: [{ key: 'embedUrl', label: tt('Odkaz na video (YouTube alebo Vimeo)'), kind: 'text' }],
}

/** Every field this editor offers for a section of `type`, in the order it is drawn. */
export function sectionFields(type: string): SectionField[] {
  return [...EVERY_TYPE, ...(PER_TYPE[type] ?? [])]
}

/*
 * ── §3.67 - the four list fields, which are most of what a page says ────────────────────────────
 */

/** One sub-field of one list entry. `multiline` gets a textarea, as the scalars do. */
export interface ListSubField {
  key: string
  label: string
  kind: 'text' | 'multiline'
}

/**
 * The list a section of this type carries, and what its entries hold - `SAAS.md` §3.67, FB-686.
 *
 * **Read off the renderer**, the same way §3.66 read the scalars: `Sections.tsx` and `Gallery.tsx`
 * decide what appears on the public site, so a sub-field offered here that nothing draws would be a
 * box whose value is invisible.
 *
 * **The server's own spelling**, which is the first alias in `site.ts`'s `FIELDS` - and the reason
 * `faq` is `q`/`a` rather than `question`/`answer`. Writing the readable names would produce a page
 * that reads correctly through the aliases and **matches no page legacy has written**.
 *
 * `nested` is the footer's case: a column holds its own `links`. One level, carried as data rather
 * than hard-coded in the UI, because a pricing tier's feature list will want the same.
 */
export interface SectionListSpec {
  key: string
  label: string
  entryLabel: string
  fields: ListSubField[]
  nested: { key: string; label: string; entryLabel: string; fields: ListSubField[] } | null
}

const ITEM_LISTS: Record<string, SectionListSpec> = {
  /* §3.129 - the two types added with this entry. Read off `Sections.tsx`'s own cases, the same way every
     descriptor here is: `stats` draws `value`/`label`/`suffix` and `steps` draws `title`/`body`.

     **`steps` has no number field on purpose.** The renderer draws `index + 1`, so a box for it would be a
     figure an operator could set to disagree with the order on screen - and legacy does not offer one either. */
  stats: {
    key: 'items',
    label: tt('Čísla'),
    entryLabel: tt('Číslo'),
    fields: [
      { key: 'value', label: tt('Hodnota'), kind: 'text' },
      { key: 'suffix', label: tt('Prípona (napríklad %)'), kind: 'text' },
      { key: 'label', label: tt('Popis'), kind: 'text' },
    ],
    nested: null,
  },
  steps: {
    key: 'items',
    label: tt('Kroky'),
    entryLabel: tt('Krok'),
    fields: [
      { key: 'title', label: tt('Titulok'), kind: 'text' },
      { key: 'body', label: tt('Text'), kind: 'multiline' },
    ],
    nested: null,
  },
  features: {
    key: 'items',
    label: tt('Vlastnosti'),
    entryLabel: tt('Vlastnosť'),
    fields: [
      // A lucide name - `ChefHat`, `Truck`. §4.4 wired the tile; an unknown name gets the fallback.
      { key: 'icon', label: tt('Ikona (názov)'), kind: 'text' },
      { key: 'title', label: tt('Titulok'), kind: 'text' },
      { key: 'body', label: tt('Text'), kind: 'multiline' },
    ],
    nested: null,
  },
  faq: {
    key: 'items',
    label: tt('Otázky'),
    entryLabel: tt('Otázka'),
    fields: [
      { key: 'q', label: tt('Otázka'), kind: 'text' },
      { key: 'a', label: tt('Odpoveď'), kind: 'multiline' },
    ],
    nested: null,
  },
  testimonials: {
    key: 'items',
    label: tt('Referencie'),
    entryLabel: tt('Referencia'),
    fields: [
      { key: 'quote', label: tt('Citát'), kind: 'multiline' },
      { key: 'author', label: tt('Autor'), kind: 'text' },
      { key: 'role', label: tt('Rola alebo firma'), kind: 'text' },
    ],
    nested: null,
  },
  logos: {
    key: 'items',
    label: tt('Logá'),
    entryLabel: tt('Logo'),
    fields: [
      { key: 'label', label: tt('Názov'), kind: 'text' },
      { key: 'href', label: tt('Odkaz'), kind: 'text' },
      { key: 'src', label: tt('Obrázok (URL)'), kind: 'text' },
    ],
    nested: null,
  },
  gallery: {
    key: 'items',
    label: tt('Galéria'),
    entryLabel: tt('Obrázok'),
    fields: [
      // `Gallery.tsx` drops an entry whose `src` is not a safe asset, so this is the one that
      // decides whether the thumbnail exists at all.
      { key: 'src', label: tt('Obrázok (URL)'), kind: 'text' },
      { key: 'caption', label: tt('Popis'), kind: 'text' },
      { key: 'alt', label: tt('Alternatívny text'), kind: 'text' },
    ],
    nested: null,
  },
  pricing: {
    // `tiers` first: the site reads `['tiers', 'plans']` and prefers the former.
    key: 'tiers',
    label: tt('Cenové plány'),
    entryLabel: tt('Plán'),
    fields: [
      { key: 'name', label: tt('Názov'), kind: 'text' },
      { key: 'monthlyPrice', label: tt('Cena za mesiac'), kind: 'text' },
      { key: 'description', label: tt('Poznámka'), kind: 'text' },
      // The slug picks the emphasised plan through `style.highlight`.
      { key: 'slug', label: tt('Slug (pre zvýraznenie)'), kind: 'text' },
    ],
    nested: null,
  },
  footer: {
    key: 'columns',
    label: tt('Stĺpce'),
    entryLabel: tt('Stĺpec'),
    fields: [{ key: 'title', label: tt('Titulok'), kind: 'text' }],
    nested: {
      key: 'links',
      label: tt('Odkazy'),
      entryLabel: tt('Odkaz'),
      fields: [
        { key: 'label', label: tt('Text'), kind: 'text' },
        { key: 'href', label: tt('Odkaz'), kind: 'text' },
      ],
    },
  },
}

/** The list this type carries, or null - six of the nine renderable types have none. */
export function sectionList(type: string): SectionListSpec | null {
  return ITEM_LISTS[type] ?? null
}

/** A section's list as a list of records, whatever the stored value turned out to be. */
export function listEntries(section: MarketingSection, key: string): Record<string, unknown>[] {
  const raw = section.props[key]
  return Array.isArray(raw)
    ? raw.map((entry) =>
        entry !== null && typeof entry === 'object' && !Array.isArray(entry)
          ? (entry as Record<string, unknown>)
          : {},
      )
    : []
}

/** What one sub-field of one entry currently holds, as text for an input. */
export function listValue(entry: Record<string, unknown>, key: string): string {
  const value = entry[key]
  if (typeof value === 'string') return value
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return ''
}

/**
 * The sections with one list rewritten - and **nothing else touched**.
 *
 * Every edit on this screen rewrites the whole `sections` array, so a mutator that rebuilt `props`
 * or an entry from the fields it knows would delete whatever else the entry carries: a `features`
 * item with a `badge` nobody edits, a tier's `moduleCount`. Both are spread.
 *
 * Shared by all four operations so there is one place that gets that right.
 */
function withList(
  sections: readonly MarketingSection[],
  id: string,
  key: string,
  change: (entries: Record<string, unknown>[]) => Record<string, unknown>[],
): MarketingSection[] {
  return sections.map((section) => {
    if (section.id !== id) return section
    return { ...section, props: { ...section.props, [key]: change(listEntries(section, key)) } }
  })
}

/**
 * One sub-field of one entry, changed.
 *
 * **An empty value removes the key**, as `sectionEdited` does: `anyField` reads a blank as absent,
 * so storing `""` is a value that means nothing, and clearing a box somebody filled by mistake
 * should leave the entry as it was before they typed.
 */
export function listEdited(
  sections: readonly MarketingSection[],
  id: string,
  key: string,
  index: number,
  field: string,
  value: string,
): MarketingSection[] {
  return withList(sections, id, key, (entries) =>
    entries.map((entry, position) => {
      if (position !== index) return entry
      const next = { ...entry }
      if (value === '') delete next[field]
      else next[field] = value
      return next
    }),
  )
}

/** An empty entry at the end. Empty rather than prefilled: a placeholder would publish as content. */
export function listAdded(
  sections: readonly MarketingSection[],
  id: string,
  key: string,
): MarketingSection[] {
  return withList(sections, id, key, (entries) => [...entries, {}])
}

export function listRemoved(
  sections: readonly MarketingSection[],
  id: string,
  key: string,
  index: number,
): MarketingSection[] {
  return withList(sections, id, key, (entries) =>
    entries.filter((_entry, position) => position !== index),
  )
}

/** One entry up or down, and a no-op at either end - `moved`'s own rule for the sections. */
export function listMoved(
  sections: readonly MarketingSection[],
  id: string,
  key: string,
  index: number,
  step: -1 | 1,
): MarketingSection[] {
  return withList(sections, id, key, (entries) => {
    const next = index + step
    if (index < 0 || index >= entries.length || next < 0 || next >= entries.length) {
      return [...entries]
    }
    const copy = [...entries]
    const [lifted] = copy.splice(index, 1)
    copy.splice(next, 0, lifted!)
    return copy
  })
}

/* The footer's second level: a column's own `links`. The same four operations, one level down. */

function withNested(
  sections: readonly MarketingSection[],
  id: string,
  key: string,
  index: number,
  nestedKey: string,
  change: (entries: Record<string, unknown>[]) => Record<string, unknown>[],
): MarketingSection[] {
  return withList(sections, id, key, (entries) =>
    entries.map((entry, position) => {
      if (position !== index) return entry
      const raw = entry[nestedKey]
      const nested = Array.isArray(raw)
        ? raw.map((one) =>
            one !== null && typeof one === 'object' && !Array.isArray(one)
              ? (one as Record<string, unknown>)
              : {},
          )
        : []
      return { ...entry, [nestedKey]: change(nested) }
    }),
  )
}

export function nestedEntries(
  entry: Record<string, unknown>,
  nestedKey: string,
): Record<string, unknown>[] {
  const raw = entry[nestedKey]
  return Array.isArray(raw)
    ? raw.map((one) =>
        one !== null && typeof one === 'object' && !Array.isArray(one)
          ? (one as Record<string, unknown>)
          : {},
      )
    : []
}

export function nestedEdited(
  sections: readonly MarketingSection[],
  id: string,
  key: string,
  index: number,
  nestedKey: string,
  nestedIndex: number,
  field: string,
  value: string,
): MarketingSection[] {
  return withNested(sections, id, key, index, nestedKey, (entries) =>
    entries.map((entry, position) => {
      if (position !== nestedIndex) return entry
      const next = { ...entry }
      if (value === '') delete next[field]
      else next[field] = value
      return next
    }),
  )
}

export function nestedAdded(
  sections: readonly MarketingSection[],
  id: string,
  key: string,
  index: number,
  nestedKey: string,
): MarketingSection[] {
  return withNested(sections, id, key, index, nestedKey, (entries) => [...entries, {}])
}

export function nestedRemoved(
  sections: readonly MarketingSection[],
  id: string,
  key: string,
  index: number,
  nestedKey: string,
  nestedIndex: number,
): MarketingSection[] {
  return withNested(sections, id, key, index, nestedKey, (entries) =>
    entries.filter((_entry, position) => position !== nestedIndex),
  )
}

/** What removing a list entry takes off the page, said before it is done. */
export function listRemoveNote(spec: SectionListSpec, index: number): string {
  return `${spec.entryLabel} ${formatCount(index + 1)} ${tt('zmizne zo sekcie. Do konceptu sa uloží hneď, na stránku až po publikovaní.')}`
}

/**
 * The three style choices the site honours, and only those.
 *
 * `site.ts` reads `align`, `padding` and `bg` off `props.style` - plus `titleSize`, which legacy
 * takes for the **hero only** because every other section hard-codes its scale, and `highlight`,
 * which picks the emphasised pricing plan and belongs with the list editor that sets the plans.
 *
 * An empty value means *inherit*, which is legacy's own `default:` arm rather than a fourth choice.
 */
export const STYLE_CHOICES: Record<string, string[]> = {
  bg: ['', 'dark', 'light', 'muted', 'gradient', 'brand'],
  align: ['', 'left', 'center', 'right'],
  padding: ['', 'tight', 'normal', 'loose'],
}

/**
 * The style keys one type adds to the three every section honours - `SAAS.md` §3.130.
 *
 * **`tone` is banner-only and that is why this function exists.** `STYLE_CHOICES` is offered on every type, so
 * putting `tone` in it would draw a select on fifteen screens that fourteen of them ignore - the same *box whose
 * value nothing draws* §3.66 named. Legacy's own four values, in its own order, with the blank meaning its
 * fallback to `brand`.
 */
const STYLE_PER_TYPE: Record<string, Record<string, string[]>> = {
  banner: { tone: ['', 'brand', 'dark', 'info', 'success'] },
  /* §3.131 - legacy reverses the split's columns on `left` and nothing else reads this. The blank is its own
     default, which is the image on the right. */
  split: { imagePosition: ['', 'right', 'left'] },
  /* §3.132 - `columns` was read by `columnsClass` and settable nowhere, while legacy has a select on each of
     these three. **The blank is not "no columns" but "let the section decide"**: `stats` then derives the count
     from how many figures it has, as legacy does, and the other two fall back to three.

     These write **strings**, because `sectionStyled` writes every style value as one - which is why §3.132 made
     `columnsClass` accept a numeric string. A select here that wrote `"4"` against a reader wanting a number
     would have looked like it worked and changed nothing. */
  stats: { columns: ['', '2', '3', '4'] },
  features: { columns: ['', '2', '3', '4'] },
  gallery: { columns: ['', '2', '3', '4'] },
}

/** Every style select this editor offers for a section of `type`, in the order it is drawn. */
export function styleChoices(
  type: string,
  section?: MarketingSection,
): Record<string, string[]> {
  const choices: Record<string, string[]> = { ...STYLE_CHOICES, ...(STYLE_PER_TYPE[type] ?? {}) }
  /* §3.133 - `pricing`'s featured tier, as a select of **the section's own tier slugs**.
     **Not legacy's control, on purpose.** It offers a free-text slug, and `isFeaturedTier` falls back silently
     when the value names no tier - so a typo looks set and does nothing. That is precisely what everybody
     believed was happening on the owner's page for a month (§3.133: it was not, but only because their slug
     happened to be right). A list of what exists cannot be mistyped.
     The blank is *no explicit highlight*, which is not the same as picking the middle tier: the fallback is
     legacy's `slug === 'advanced' || index === 1`. */
  if (type === 'pricing' && section !== undefined) {
    const slugs = listEntries(section, 'tiers').length > 0 ? listEntries(section, 'tiers') : listEntries(section, 'plans')
    const named = slugs
      .map((tier) => (typeof tier.slug === 'string' ? tier.slug.trim() : ''))
      .filter((slug) => slug !== '')
    if (named.length > 0) choices.highlight = ['', ...named]
  }
  return choices
}

export const HERO_TITLE_SIZES = ['', 'lg', 'xl', 'xxl']

/** A section's `style` object as a plain record, whatever the stored value turned out to be. */
export function styleOf(section: MarketingSection): Record<string, unknown> {
  const raw = section.props.style
  return raw !== null && typeof raw === 'object' && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {}
}

/**
 * One field of one section, changed - and **nothing else touched**.
 *
 * The whole `sections` array is what `PUT …/marketing-pages/{slug}` stores, so every edit rewrites
 * the array: a mutator that dropped a key it did not know about would delete the list content this
 * increment deliberately does not edit. `props` is spread, never rebuilt.
 *
 * An empty string **removes** the key rather than storing `""`. The renderer's `prop()` treats a
 * blank as absent already, so storing one would be a value that reads as nothing - and clearing a
 * field somebody typed by mistake should leave the page as it was before they typed it.
 */
export function sectionEdited(
  sections: readonly MarketingSection[],
  id: string,
  key: string,
  value: string | boolean,
): MarketingSection[] {
  return sections.map((section) => {
    if (section.id !== id) return section
    const props = { ...section.props }
    if (value === '' || value === false) {
      delete props[key]
    } else {
      props[key] = value
    }
    return { ...section, props }
  })
}

/** One key of one section's `style`, changed. Same rule: an empty value is a removal. */
export function sectionStyled(
  sections: readonly MarketingSection[],
  id: string,
  key: string,
  value: string,
): MarketingSection[] {
  return sections.map((section) => {
    if (section.id !== id) return section
    const style = { ...styleOf(section) }
    if (value === '') {
      delete style[key]
    } else {
      style[key] = value
    }
    const props = { ...section.props }
    if (Object.keys(style).length === 0) {
      delete props.style
    } else {
      props.style = style
    }
    return { ...section, props }
  })
}

/**
 * A key whose value is a URL, which a label cannot stand in for - FB-852, §3.161.
 *
 * `safeAnchorOrAsset` and `safeAsset` refuse anything that is not a usable target (§3.151), so a label here would
 * draw nothing and leave the operator debugging a broken link instead of filling a box. **Absent is the honest
 * state**, and it is also what lets §3.160's pricing CTA fall back to the contact address rather than to the
 * string *CTA na tarife - odkaz*.
 */
function holdsATarget(key: string): boolean {
  return key === 'src' || key.endsWith('Url') || key.endsWith('Href')
}

/** One field's placeholder, or `null` where the honest default is no value at all. */
function placeholderFor(field: { key: string; label: string; kind: string; choices?: readonly { value: string }[] }) {
  if (holdsATarget(field.key)) return null
  /* A `choice` takes its first option's **value**, not its label, and this is the rule that does real work:
     `pricing`'s only per-type field is `source`, whose first choice is `live`, so a new pricing section resolves
     the owner's real packages server-side (§3.142) instead of drawing an empty list. A label there would be a
     value the server does not recognise. */
  if (field.kind === 'choice') return field.choices?.[0]?.value ?? null
  return field.label
}

/**
 * What a newly added section starts with - FB-852, {@code SAAS.md §3.161}.
 *
 * <p><b>It used to be `{}`, and there is no preview in the Tower</b>, so an operator's only way to see what a
 * section type does was to publish it to the live public site - where an empty section renders a band of whitespace,
 * because every branch inside `SectionView` is a `!== null` that fails.
 *
 * <p><b>Derived from the editor's own descriptors rather than a hand-written map.</b> `sectionFields` and
 * `sectionList` are what the operator is shown, so a default **cannot name a key the renderer does not read** - the
 * failure §3.132 and §3.130 are both about - and a type added later gets one with no second list to remember.
 *
 * <p><b>The placeholder is the field's own label</b>, which is legacy's pattern generalised (its `footer` default is
 * `tagline: "Tagline"`, its `faq` is `title: "FAQ"`) and the half that makes this safe: a heading reading
 * <i>Titulok</i> is nobody's finished copy.
 *
 * <p><b>Legacy's actual values are deliberately not ported.</b> <i>"Najlepšie rozhodnutie tento rok." - Klient,
 * CEO</i> is indistinguishable from a real testimonial to a visitor, and legacy publishes it the moment somebody
 * adds a quote section and forgets; `"© 2026"` is wrong next January. **Trading *nothing appears* for *something
 * false appears* is worse on a public page** - §3.130's argument about the `dismissable` label and §3.150's about
 * the e-mail box.
 *
 * <p><b>One entry in an item list, not none</b>: a `faq` with an empty `items` renders nothing whatever its title
 * says, so the single entry is what makes the section visible at all.
 */
export function blankPropsFor(type: string): Record<string, unknown> {
  const props: Record<string, unknown> = {}
  for (const field of sectionFields(type)) {
    const value = placeholderFor(field)
    if (value !== null) props[field.key] = value
  }
  const list = sectionList(type)
  if (list !== null) {
    const entry: Record<string, unknown> = {}
    for (const field of list.fields) {
      const value = placeholderFor({ ...field, choices: undefined })
      if (value !== null) entry[field.key] = value
    }
    props[list.key] = [entry]
  }
  return props
}

/**
 * A new section, appended.
 *
 * **The id is generated here and it matters**: `sections` is keyed by it in React, the reorder
 * arrows address it, and the renderer's `anchorId` is a different thing entirely. `crypto.randomUUID`
 * is what the browser has; legacy generates its own the same way.
 *
 * **Only a type the site can draw**, because the alternative is a section that saves, publishes and
 * never appears - the failure `MARKETING_SECTION_TYPES`' own docstring calls *silent in the direction
 * that matters*.
 */
export function withSectionAdded(
  sections: readonly MarketingSection[],
  type: string,
  id: string,
): MarketingSection[] {
  if (!(KNOWN_SECTIONS as readonly string[]).includes(type)) return [...sections]
  /* FB-852, §3.161 - this was `props: {}`, and with no preview in the Tower the operator's only way to see a
     section was to publish an empty one to the live site. */
  return [...sections, { id, type, props: blankPropsFor(type) }]
}

/** What a field holds now, as the input wants it. */
export function fieldValue(section: MarketingSection, key: string): string {
  const raw = section.props[key]
  return typeof raw === 'string' ? raw : ''
}

/** What a boolean field holds now - only an explicit `true` counts, as the renderer reads it. */
export function flagValue(section: MarketingSection, key: string): boolean {
  return section.props[key] === true
}

/**
 * One `style` key as a string, or `''`.
 *
 * `props.style` is a jsonb blob: a key can hold a number, an object, anything. `site.ts`'s own
 * `styleWord` checks `typeof value === 'string'` for exactly that reason, and a `<select>` whose
 * value came out as `[object Object]` would silently offer to store that back.
 */
export function styleWordOf(section: MarketingSection, key: string): string {
  const value = styleOf(section)[key]
  return typeof value === 'string' ? value : ''
}
